import React from "react";
import { Box, Slider, Typography } from "@mui/material";

import type { UnitSystem } from "../../utils/units";
import {
  droughtIndexLabel,
  droughtIndexScaleMax,
  formatDroughtIndex,
  fromDisplayDroughtIndex,
  toDisplayDroughtIndex,
} from "../../utils/units";

interface DroughtIndexControlProps {
  /** Current value in millimetres, or null when nothing has set it yet. */
  value: number | null;
  onChange: (mm: number) => void;
  unitSystem: UnitSystem;
  /** Where the starting value came from, e.g. "From the forecast for this date". */
  source?: string | null;
  /** Compact layout for a row of controls. */
  dense?: boolean;
  /** Dark weather-panel styling, matching the surrounding sliders. */
  dark?: boolean;
}

/**
 * Keetch-Byram Drought Index for a simulation.
 *
 * The drought factor says how dry the fine fuel is this week; this says how
 * deep the dry has gone. Wet forest only carries fire once it is high, so it
 * sits beside the drought factor rather than being folded into it.
 */
const DroughtIndexControl: React.FC<DroughtIndexControlProps> = ({
  value,
  onChange,
  unitSystem,
  source,
  dense = false,
  dark = true,
}) => {
  const subtle = dark ? "rgba(255, 255, 255, 0.6)" : "text.secondary";
  const faint = dark ? "rgba(255, 255, 255, 0.5)" : "text.disabled";
  const strong = dark ? "#fff" : "text.primary";
  const accent = "#c77d2e";

  const max = droughtIndexScaleMax(unitSystem);
  const shown = value === null ? 0 : toDisplayDroughtIndex(value, unitSystem);
  const unset = value === null;
  const helper = unset
    ? "Nothing has set this yet. Leave it and the model assumes it from the drought factor, or set the deep dryness you expect."
    : source ?? null;

  const slider = (
    <Slider
      value={shown}
      onChange={(_, next) => onChange(fromDisplayDroughtIndex(next as number, unitSystem))}
      min={0}
      max={max}
      step={unitSystem === "imperial" ? 4 : 1}
      sx={{
        color: accent,
        opacity: unset ? 0.5 : 1,
        "& .MuiSlider-thumb": { width: dense ? 16 : 20, height: dense ? 16 : 20 },
        "& .MuiSlider-track": { height: dense ? 4 : 6 },
        "& .MuiSlider-rail": {
          height: dense ? 4 : 6,
          bgcolor: dark ? "rgba(255, 255, 255, 0.1)" : undefined,
        },
      }}
    />
  );

  if (dense) {
    return (
      <Box>
        <Typography variant="caption" sx={{ color: subtle }}>
          Drought Index (KBDI):{" "}
          <b style={{ color: dark ? "#fff" : undefined }}>
            {unset ? "—" : formatDroughtIndex(value, unitSystem)}
          </b>
          {!unset && <span style={{ color: accent }}> · {droughtIndexLabel(value)}</span>}
        </Typography>
        {slider}
        {helper && (
          <Typography variant="caption" sx={{ color: faint, display: "block" }}>
            {helper}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: subtle, textTransform: "uppercase", fontSize: "0.75rem" }}
          >
            Drought Index (KBDI)
          </Typography>
          <Typography variant="h4" sx={{ color: strong, fontWeight: 600 }}>
            {unset ? "—" : formatDroughtIndex(value, unitSystem)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
            {unset ? "Not set" : droughtIndexLabel(value)}
          </Typography>
          <Typography variant="caption" sx={{ color: faint, display: "block" }}>
            Deep soil and litter dryness
          </Typography>
        </Box>
      </Box>
      {helper && (
        <Typography variant="caption" sx={{ color: faint, display: "block", mb: 1.5 }}>
          {helper}
        </Typography>
      )}
      <Box sx={{ mt: "auto" }}>
      {slider}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="caption" sx={{ color: faint }}>
          0 (Saturated)
        </Typography>
        <Typography variant="caption" sx={{ color: faint }}>
          {toDisplayDroughtIndex(fromDisplayDroughtIndex(max / 2, unitSystem), unitSystem)}
        </Typography>
        <Typography variant="caption" sx={{ color: faint }}>
          {unitSystem === "imperial" ? "800 (Bone dry)" : "203 mm (Bone dry)"}
        </Typography>
      </Box>
      </Box>
    </Box>
  );
};

export default DroughtIndexControl;
